import {UIWRoomUserListController} from './UIWRoomUserListController'
import {UIWRoomCenterController} from './UIWRoomCenterController'
import {RoomState} from '../../../../../shared/RoomState'
import {UIManager} from '../UIManager'
import {Scene} from '../UISceneTypes'
import {UIRoomChatController} from './UIRoomChatController'
import {ObjectUtils} from '@colonist/utils'
import {Sound} from '../../../../shared/scripts/Sound'
import SoundType = Sound.SoundType
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {SocketLobbySend} from '../../../../shared/scripts/socket/SocketLobbySend'
import {PIXI} from '@colonist/pixi'
import {removeHash} from '../../../../shared/scripts/ui_utils/Utils'
import {FriendStatusData} from '../../../../../shared/friends/FriendTypes'
import {BigId} from '@colonist/types'
import {UIRoomFriendListController} from '../../../../shared/scripts/ui_html/room/UIRoomFriendListController'
import {UIRoomPlayOrderSelectionController} from './play_order_selection/UIRoomPlayOrderSelectionController'
import {RoomPlayOrderSelectionData, RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {UIDRoomFriendListController} from './UIDRoomFriendListController'
import {UIRoomSettingsController} from '../../../../shared/scripts/ui_room/UIRoomSettingsController'

export class UIRoomController {
    private readonly container: HTMLDivElement
    private readonly userList: UIWRoomUserListController
    private readonly friendList: UIRoomFriendListController
    readonly chat: UIRoomChatController
    center: UIWRoomCenterController

    private roomInitiated: boolean = false
    private readonly uiManager: UIManager
    private playOrderSelectionController: UIRoomPlayOrderSelectionController
    private roomState: RoomState

    constructor(uiManager: UIManager) {
        this.uiManager = uiManager
        this.container = document.getElementById('scene_room_container') as HTMLDivElement

        this.userList = new UIWRoomUserListController()
        this.chat = new UIRoomChatController()
        this.center = new UIWRoomCenterController()

        const friendListContainer = document.getElementById('scene_room_friend_list') as HTMLDivElement
        const friendHeader = document.getElementById('scene_room_left_friend_header') as HTMLElement
        const friendPlayerList = document.getElementById('scene_room_player_list') as HTMLDivElement
        this.friendList = UIUtilsEjsVariables.isUserOnDiscord ? new UIDRoomFriendListController(friendListContainer, friendHeader, friendPlayerList) : new UIRoomFriendListController(friendListContainer, friendHeader, friendPlayerList)

        this.updatePageOnHashChange()
    }

    hide() {
        this.container.style.display = 'none'
        this.userList.clear()
    }

    async show() {
        this.container.style.display = 'flex'
        this.userList.load(this.roomState)
        await this.center.load(this.roomState)
        this.friendList?.updateRoomState(this.roomState)
    }

    updateRoom(roomState: RoomState) {
        this.refreshBrowserIfCurrentSceneIsGame()
        if(this.roomInitiated) {
            void this.updateCurrentRoom(roomState)
            return
        }
        this.initRoom(roomState)
    }

    updateSeedOrderSelection(data: RoomSeedOrderSelectionData) {
        this.center.updateSeedOrderSelection(this.roomState, data)
        this.playOrderSelectionController.seedOrder = data
        void this.center.update(undefined, this.roomState)
    }

    updatePlayOrderSelection(data: RoomPlayOrderSelectionData) {
        this.playOrderSelectionController.update(this.roomState, data)
    }

    leaveRoom(toScene?: Scene, data?: Record<string, unknown>) {
        toScene ??= Scene.Lobby
        this.resetRoom()
        this.uiManager.sceneController.loadScene(toScene, data)
    }

    resetRoom() {
        SocketLobbySend.clickedRoomExit()
        this.roomInitiated = false
        this.userList.clear()
        this.chat.clear()
        removeHash()
    }

    private initRoom(roomState: RoomState) {
        UIRoomController.updateURLToRoom(roomState.gameSetting.id)
        this.roomInitiated = true
        this.roomState = roomState

        this.friendList.setRoomId(roomState.gameSetting.id)

        UIRoomSettingsController.initRoomSettingFromLocalStorage()
        this.playOrderSelectionController = new UIRoomPlayOrderSelectionController()
        this.uiManager.sceneController.loadScene(Scene.Room)

        this.friendList?.animateInviteFriendsButton()
    }

    private async updateCurrentRoom(newRoomState: RoomState) {
        this.userList.update(newRoomState)
        this.friendList?.updateRoomState(newRoomState)
        await this.center.update(this.roomState, newRoomState)
        UIRoomController.playUpdateSounds(this.roomState, newRoomState)
        this.roomState = newRoomState
    }

    private refreshBrowserIfCurrentSceneIsGame() {
        const isUserInGame = this.uiManager.sceneController.scene == Scene.Game
        if(!isUserInGame) return
        location.reload()
    }

    private static playUpdateSounds(oldRoomState: RoomState, newRoomState: RoomState) {
        const settingsChanged = !ObjectUtils.isObjectEqual(oldRoomState.gameSetting, newRoomState.gameSetting)
        if(settingsChanged) {
            Sound.playSound(SoundType.RoomSettingUpdated)
            return
        }

        const previouslyReadyPlayers = oldRoomState.getSessions()
            .filter((session) => session.isReadyToPlay)
            .length

        const nowReadyPlayers = newRoomState.getSessions()
            .filter((session) => session.isReadyToPlay)
            .length

        if(nowReadyPlayers > previouslyReadyPlayers) {
            Sound.playSound(SoundType.RoomGetReady)
        }
    }

    static updateURLToRoom(gameId: string) {
        if(window.location.hash == '#' + gameId) return
        window.location.hash = gameId
    }

    private updatePageOnHashChange() {
        window.onhashchange = (hashChangeEvent: HashChangeEvent) => {
            const oldUrl = new URL(hashChangeEvent.oldURL)
            const newUrl = new URL(hashChangeEvent.newURL)

            const adHash = '#goog_rewarded'
            if(newUrl.hash == adHash || oldUrl.hash == adHash) {
                history.replaceState(null, document.title, window.location.pathname + window.location.search)
                return
            }

            const isTheSameHash = oldUrl.hash.toLocaleLowerCase() == newUrl.hash.toLocaleLowerCase()
            if(isTheSameHash) return

            const hashesNotEmpty = oldUrl.hash != '' && newUrl.hash != ''

            if(hashesNotEmpty) {
                location.reload()
                return
            }

            const sceneIsRoom = this.uiManager.sceneController.scene == Scene.Room
            const sceneIsGame = this.uiManager.sceneController.scene == Scene.Game
            const roomAlreadyInitiated = sceneIsRoom && this.roomInitiated

            const newHashIsSet = newUrl.hash != ''

            if(roomAlreadyInitiated) return
            if(sceneIsGame && newHashIsSet) return

            location.reload()
        }
    }

    static showHeaderInRoom(): boolean {
        if(UIUtilsEjsVariables.isUserOnDiscordMobile) return true
        if(UIUtilsEjsVariables.isUserOnMobileApp) return false
        if(PIXI.utils.isMobile.phone) return false
        return true
    }

    updateAllFriendsOnlineStatus(friends: FriendStatusData[]) {
        this.friendList?.updateFriendsList(friends)
    }

    updateFriendOnlineStatus(friend: FriendStatusData) {
        this.friendList?.updateFriendOnlineStatus(friend)
    }

    toggleFriendInviteButtonState(responderUserId: BigId, enable: boolean) {
        this.friendList?.toggleFriendInviteButtonState(responderUserId, enable)
    }
}
